import { AlertCircle, Download, Loader2 } from "lucide-react";
import { Cell, Pie, PieChart, type PieLabelRenderProps } from "recharts";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { DataTable } from "@/components/ui/data-table";

import type { ColumnDef } from "@tanstack/react-table";

interface DataSectionProps {
  filteredData: Array<{
    modality: string;
    amount: number;
    color: string;
    percentage: string | number;
  }>;
  totalStudies: number;
  errorMetrics: Error | null;
  isLoading: boolean;
  getPeriodText: () => string;
  chartConfig: Record<string, { label: string; color: string }>;
  columns: ColumnDef<{
    modality: string;
    amount: number;
    color: string;
    percentage: string | number;
  }>[];
  handleDownloadExcel: () => void;
  loadingExcel: boolean;
  institutionName: string;
  errorChartModalitiesConfig: Error | null;
}

/**
 * Componente para la sección de datos y gráficos del reporte de negocio
 * (métricas desde /api/v1/imaging/metrics/dashboard)
 */
export function DataSection({
  filteredData,
  totalStudies,
  errorMetrics,
  isLoading,
  getPeriodText,
  chartConfig,
  columns,
  errorChartModalitiesConfig,
  handleDownloadExcel,
  loadingExcel,
  institutionName,
}: DataSectionProps): React.JSX.Element {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Gráfico de Pie */}

      <Card className="min-h-[600px]">
        <CardHeader>
          <CardTitle>Distribución por Modalidad</CardTitle>
          <CardDescription>
            {getPeriodText()} - {filteredData.length} modalidades
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center">
            <span className="text-lg font-medium bold text-gray-900 dark:text-gray-100">
              {institutionName}
            </span>
          </div>
          <ChartContainer config={chartConfig} className="min-h-20">
            {isLoading ? (
              <div className="flex items-center justify-center">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            ) : errorChartModalitiesConfig ? (
              <div className="flex items-center justify-center">
                <AlertCircle className="h-4 w-4 text-red-500" />
                Error al cargar los datos: {errorChartModalitiesConfig.message}
              </div>
            ) : errorMetrics ? (
              <div className="flex items-center justify-center gap-2 text-sm text-red-500">
                <AlertCircle className="h-4 w-4" />
                Error al cargar las métricas: {errorMetrics.message}
              </div>
            ) : (
              <PieChart>
                <Pie
                  data={filteredData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(props: PieLabelRenderProps) => {
                    const entry = props.payload as {
                      modality: string;
                      percentage: string | number;
                      amount: number;
                    };
                    return `${entry.modality}: ${entry.amount} (${entry.percentage}%)`;
                  }}
                  outerRadius={100}
                  fill="var(--chart-1)"
                  dataKey="amount"
                  nameKey="modality"
                >
                  {filteredData.map((data, index) => (
                    <Cell key={`cell-${index}`} fill={data.color} />
                  ))}
                </Pie>
                <ChartTooltip
                  content={({ active, payload }) => {
                    if (active && payload?.length) {
                      return (
                        <ChartTooltipContent
                          active={active}
                          payload={payload}
                          label={payload[0].name}
                          formatter={(value, name) => [
                            `${value.toLocaleString()} (${(payload[0].payload as (typeof filteredData)[0]).percentage}%)`,
                            name,
                          ]}
                        />
                      );
                    }
                    return null;
                  }}
                />
                <ChartLegend
                  content={({ payload }) => (
                    <ChartLegendContent
                      payload={
                        payload as unknown as Array<{
                          value?: string | number;
                          dataKey?: string | number | ((obj: unknown) => unknown);
                          color?: string;
                          [key: string]: unknown;
                        }>
                      }
                      nameKey="modality"
                    />
                  )}
                />
              </PieChart>
            )}
          </ChartContainer>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-2">
              <CardTitle>Detalle de Modalidades</CardTitle>
              <CardDescription>
                {`Lista de modalidades filtradas por institución o médico y período`}
              </CardDescription>
            </div>
            <Button
              variant="outline"
              className="flex items-center gap-2"
              onClick={handleDownloadExcel}
              disabled={loadingExcel}
            >
              <Download className="h-4 w-4" />
              Descargar Excel
            </Button>
          </div>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {errorMetrics ? (
            <div className="text-sm text-red-500">
              Error al cargar los datos: {errorMetrics.message}
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={filteredData}
              searchKey="modality"
              searchPlaceholder="Buscar por modalidad..."
              loading={isLoading}
              showPagination={true}
              showSearch={true}
              showFilters={true}
              pageSize={10}
              emptyMessage="No se encontraron datos para los filtros seleccionados"
              className="w-full"
            />
          )}
          <div className="flex items-center justify-center">
            <span className="text-lg font-medium text-gray-900 dark:text-gray-100">
              Total de estudios: {totalStudies}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
